import { toDecimalString, toDecimalFloat, toCurrency } from '../transformers'

const VALUE_TYPE_FORMAT_FUNCTION = {
  INTEGER: (value: number) => toDecimalString(value, 0),
  FLOAT: (value: number) => toDecimalFloat(value, 2),
  PERCENTAGE: (value: number) => `${toDecimalFloat(value, 1)}%`,
  CURRENCY: (value: number) => toCurrency(value),
}

export type ValueType = keyof typeof VALUE_TYPE_FORMAT_FUNCTION

export const formatNumber = (
  value: number,
  valueType?: keyof typeof VALUE_TYPE_FORMAT_FUNCTION
) => {
  const formatValue = valueType
    ? VALUE_TYPE_FORMAT_FUNCTION[valueType]
    : undefined
  const formattedValue = formatValue ? formatValue(value) : value
  return String(formattedValue).replace('.', ',')
}

/**
 * Formats a number as a percentage keeping 2 decimal places without rounding (truncates instead)
 * @param value - Number to be formatted (ex: 1.85)
 * @returns Formatted string with comma as decimal separator (ex: "1,85%")
 */
export const formatPercentage = (value: number): string => {
  const truncatedValue = Math.floor(value * 100) / 100
  const formattedValue = truncatedValue.toFixed(2).replace('.', ',')
  return `${formattedValue}%`
}

export const formattedOrderNumber = (number: string) =>
  number.match(/.{1,3}/g)?.join(' ')

export const removeSpecialChars = (string: string) =>
  string.replace(/[^\w\s]/gi, '')

const getSafeString = (value: unknown) => {
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'bigint') {
    return String(value)
  }

  return ''
}

export const normalizeCnpj = (value: unknown): string =>
  getSafeString(value)
    .toUpperCase()
    .replace(/[./\-\s]+/g, '')

export const sanitizeCnpjInput = (value: unknown): string =>
  getSafeString(value)
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 14)

export const isCnpjDocument = (value: unknown): boolean =>
  /^[A-Z0-9]{12}[0-9]{2}$/.test(normalizeCnpj(value))

export const getFirstName = (fullName: string) => fullName?.split(' ')[0] ?? ''
